import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["active", "aria-checked", "block", "children", "disabled", "download", "external", "hideExternalLinkIcon", "href", "iconProps", "id", "onBlur", "onClick", "onFocus", "onKeyDown", "onKeyUp", "onMouseDown", "ping", "referrerPolicy", "responsive", "size", "tabIndex", "target", "truncate", "truncateStringProps", "truncateTooltip", "use", "rel"];
import { forwardRef, useEffect, useImperativeHandle, useRef, useMemo } from 'react';
import { getSizeTokens, getUseTokens, isLinkUse, isFormUse, shouldButtonBeUnderlined, setAsActive, isTransparentUse, isLinkLikeUse, getControlMapping, isControlUse } from './Button.helpers';
import { renderChildren, renderExternalButtonIcon, renderTruncatedContents } from './Button.utils';
import styled, { useTheme } from 'foundations-theming-specialty/utils';
import { useResponsiveStyles, useSizeCssObject, useUseVariantCssObject } from './Button.hooks';
import { customizeStylesWithImportant } from '../../../utils/customizeStylesWithImportant';
import emptyFunction from 'react-utils/emptyFunction';
import { filterProps } from '../../../utils/filterProps';
import { mergeProps } from 'react-aria';
import { useIconButtonContext } from '../IconButton/IconButtonProvider';
import { useLoadingButtonContext } from '../LoadingButton/LoadingButtonProvider';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { deepMerge } from '../../utils/deepMerge';
import { useAbstractDropdownContext } from '../AbstractDropdown/AbstractDropdownProvider';
import { useSelectContext } from '../Select/SelectProvider';
import { getFocusStyleObject } from '../../utils/focus/focus.style';
import { useDropdownContext } from '../Dropdown/DropdownContext';
import { useControlContext } from '../Link/ControlContext';
import { StyledIconWrapper } from '../Link/Link';
import { TRANSITION_ALL_STYLE } from '../../../constants/transition-all';
import { jsxs as _jsxs } from "react/jsx-runtime";
// Copy and pasted from DoNotUse_Link.helpers.ts since DoNotUse_Link.helpers.ts is only meant to be used within a DataGrid**
/**
 * Checks if the given URL is attempting to run JavaScript code (other than no-op code).
 * This function is used to prevent XSS attacks.
 * @param url - The URL to check.
 * @returns A boolean indicating if the URL is safe.
 */
export const getIsSafeUrl = url => {
  if (url === undefined) {
    return true;
  }

  // eslint-disable-next-line no-script-url
  const noopInjectedJavaScript = 'javascript:void(0)';

  // Matches the protocol part of an absolute URL, e.g. "http:"
  const protocolRegex = /^([^:]+):/;

  // Whitespace and control characters are ignored, so this strips them out
  // eslint-disable-next-line no-control-regex
  const IGNORED_PROTOCOL_CHARS_REGEX = /[\s\x00-\x1f]/g;
  const matchesProtocol = url.match(protocolRegex);
  if (!matchesProtocol || url === noopInjectedJavaScript) {
    return true;
  }
  const isInjectedJavaScript = matchesProtocol[0].replace(IGNORED_PROTOCOL_CHARS_REGEX, '')
  // eslint-disable-next-line no-script-url
  .toLowerCase() === 'javascript:';
  return !isInjectedJavaScript;
};
export const UnstyledButtonLink = styled.a.withConfig({
  displayName: "PrivateButtonLink__UnstyledButtonLink"
})(({
  $block,
  $responsiveStyles,
  $tokenCssStyles,
  $isLinkUse,
  $styleOverrides,
  $alwaysUnderline,
  $isInDropdown,
  theme
}) => {
  return deepMerge(Object.assign({
    all: 'unset',
    background: 'none',
    border: 0,
    padding: 0,
    cursor: 'pointer',
    textAlign: 'center',
    textDecoration: $alwaysUnderline ? 'underline' : 'none'
  }, $tokenCssStyles, $responsiveStyles, $block && {
    width: '100%'
  }, {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  }, $isLinkUse && {
    fontWeight: theme.typography['body-125'].fontWeight,
    fontSize: 'inherit',
    lineHeight: 'inherit'
  }, {
    '&:hover': {
      textDecoration: 'underline',
      color: 'inherit'
    },
    // Override global a:hover styles for disabled buttons to prevent underlines
    '&:hover[aria-disabled="true"]': {
      textDecoration: 'none',
      cursor: 'not-allowed'
    }
  }, !$isInDropdown && {
    // allows buttons and links to have the same boldness within Dropdown
    '-webkit-font-smoothing': 'auto',
    '-moz-osx-font-smoothing': 'auto'
  }, getFocusStyleObject({
    theme,
    addBorderRadius: true
  })), $styleOverrides);
});
export const StyledButtonLink = styled.a.withConfig({
  displayName: "PrivateButtonLink__StyledButtonLink"
})(({
  $alwaysUnderline,
  $block,
  $responsiveStyles,
  $sizeCssStyles,
  $tokenCssStyles,
  $typography,
  $variantCssStyles,
  $isLinkUse,
  $isTransparentUse,
  theme,
  $styleOverrides,
  $use,
  $size,
  $isInDropdown
}) => {
  return deepMerge(Object.assign({}, $variantCssStyles, $sizeCssStyles, $tokenCssStyles, $responsiveStyles, theme.typography[$typography], $size === 'small' && {
    fontWeight: theme.typography['body-700'].fontWeight
  }, $block && {
    width: '100%'
  }, $isLinkUse ? {
    fontWeight: theme.typography['body-125'].fontWeight,
    fontSize: 'inherit',
    lineHeight: 'inherit'
  } : {
    // Buttons with link variants should not have default spacing between children in order to match the Link component's behavior
    // Buttons that have an href and external=true render an ExternalLinkIcon, which should not receive this margin as it has its own margin
    [`& > *:not(:first-child):not(${StyledIconWrapper})`]: {
      marginInlineStart: theme.space['200']
    }
  }, $isTransparentUse && {
    fontWeight: theme.typography['body-125'].fontWeight
  }, {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: isLinkUse($use) || isFormUse($use) ? 'left' : 'center',
    textAlign: isLinkUse($use) || isFormUse($use) ? 'left' : 'center',
    textDecoration: $alwaysUnderline ? 'underline' : 'none',
    transition: TRANSITION_ALL_STYLE,
    borderRadius: theme.borderRadius['100']
  }, !$isInDropdown && {
    // allows buttons and links to have the same boldness within Dropdown
    '-webkit-font-smoothing': 'auto',
    '-moz-osx-font-smoothing': 'auto'
  }, getFocusStyleObject({
    theme,
    addBorderRadius: isLinkLikeUse($use),
    hasTransitionAll: true
  })), $styleOverrides);
});
export const PrivateButtonLink = /*#__PURE__*/forwardRef((_ref, ref) => {
  var _controlContext$isCon, _selectContext$button;
  let {
      active = false,
      'aria-checked': ariaChecked,
      block,
      children,
      disabled,
      download,
      external,
      hideExternalLinkIcon = false,
      href: hrefProp,
      iconProps,
      id,
      onBlur = emptyFunction,
      onClick = emptyFunction,
      onFocus = emptyFunction,
      onKeyDown = emptyFunction,
      onKeyUp = emptyFunction,
      onMouseDown = emptyFunction,
      ping,
      referrerPolicy,
      responsive,
      size,
      tabIndex,
      target,
      truncate,
      truncateStringProps,
      truncateTooltip,
      use: rawUse,
      rel = target === '_blank' ? 'noopener noreferrer' : undefined
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const linkElementRef = useRef(null);
  const controlContext = useControlContext();
  const isControl = (_controlContext$isCon = controlContext === null || controlContext === void 0 ? void 0 : controlContext.isControl) !== null && _controlContext$isCon !== void 0 ? _controlContext$isCon : isControlUse(rawUse);
  const use = isControl ? getControlMapping(rawUse) : rawUse;
  const useTokens = getUseTokens(use);
  if (active) {
    setAsActive(useTokens);
  }
  const linkProps = Object.assign({
    onBlur,
    onClick,
    onFocus,
    onKeyDown,
    onKeyUp,
    onMouseDown
  }, rest);
  const useVariantCssStyles = useUseVariantCssObject(isLinkUse(use), useTokens);
  const sizeTokens = getSizeTokens(size, use);
  const sizeCssStyles = useSizeCssObject(sizeTokens);
  const iconButtonContext = useIconButtonContext();
  const loadingButtonContext = useLoadingButtonContext();
  const abstractDropdownContext = useAbstractDropdownContext();
  const selectContext = useSelectContext();
  const dropdownContext = useDropdownContext();
  const theme = useTheme();
  const selectButtonStyleOverrides = (_selectContext$button = selectContext === null || selectContext === void 0 ? void 0 : selectContext.buttonStyleOverrides) !== null && _selectContext$button !== void 0 ? _selectContext$button : {};
  const {
    tokenProps: selectTokenProps
  } = filterProps(selectButtonStyleOverrides);
  const {
    domProps,
    tokenProps,
    callbackProps
  } = filterProps(linkProps);
  const sharedStyleOverrides = useMemo(() => {
    var _iconButtonContext$bu, _loadingButtonContext;
    return deepMerge((_iconButtonContext$bu = iconButtonContext === null || iconButtonContext === void 0 ? void 0 : iconButtonContext.buttonStyleOverrides) !== null && _iconButtonContext$bu !== void 0 ? _iconButtonContext$bu : {}, (_loadingButtonContext = loadingButtonContext === null || loadingButtonContext === void 0 ? void 0 : loadingButtonContext.getButtonStyleOverrides(theme)) !== null && _loadingButtonContext !== void 0 ? _loadingButtonContext : {});
  }, [iconButtonContext === null || iconButtonContext === void 0 ? void 0 : iconButtonContext.buttonStyleOverrides, loadingButtonContext, theme]);
  const newSharedStyleOverrides = useMemo(() => {
    var _abstractDropdownCont;
    return deepMerge(sharedStyleOverrides, (_abstractDropdownCont = abstractDropdownContext === null || abstractDropdownContext === void 0 ? void 0 : abstractDropdownContext.buttonStyleOverrides) !== null && _abstractDropdownCont !== void 0 ? _abstractDropdownCont : {});
  }, [sharedStyleOverrides, abstractDropdownContext === null || abstractDropdownContext === void 0 ? void 0 : abstractDropdownContext.buttonStyleOverrides]);
  const selectButtonOverrides = useMemo(() => deepMerge(newSharedStyleOverrides, selectTokenProps), [newSharedStyleOverrides, selectTokenProps]);
  const customizeStyles = customizeStylesWithImportant(/^margin/);
  const tokenCssStyles = useMapTokenPropsToCSS(tokenProps, {
    customizeStyles
  });
  const responsiveStyles = useResponsiveStyles({
    responsive,
    use
  });
  const href = getIsSafeUrl(hrefProp) ? hrefProp : undefined;
  const isPlaceholder = href === undefined;

  /*
   * Adds the `ping` attribute to the `a` element when the `ping` prop changes.
   * This fixes the bug reported in https://git.hubteam.com/HubSpot/foundations-components/issues/677
   */
  useEffect(() => {
    if (ping !== undefined) {
      var _linkElementRef$curre;
      (_linkElementRef$curre = linkElementRef.current) === null || _linkElementRef$curre === void 0 || _linkElementRef$curre.setAttribute('ping', ping);
    }
  }, [ping]);
  useImperativeHandle(ref, () => linkElementRef.current, [linkElementRef]);
  const handleClick = e => {
    if (isPlaceholder) {
      e.stopPropagation(); // Prevents the click event from bubbling up when the link is a placeholder
      e.preventDefault(); // Prevents the link from navigating on click when the link is a placeholder
    } else if (typeof onClick === 'function') {
      onClick(e);
    }
  };
  const handleKeyDown = e => {
    if (isPlaceholder) {
      // We don't want to `preventDefault` because it would prevent keyboard and screen reader navigation

      e.stopPropagation(); // Prevents the keydown event from bubbling up when the link is a placeholder
    } else if (typeof onKeyDown === 'function') {
      onKeyDown(e);
    }
  };
  const mergedProps = mergeProps(domProps, callbackProps);
  const renderedChildren = renderChildren(children);

  /**
   * Set the size of the ExternalLinkIcon to the same width and height as other icons.
   */
  const renderedExternalButtonIcon = !hideExternalLinkIcon ? renderExternalButtonIcon(external, Object.assign({
    size: sizeTokens.iconSize
  }, iconProps)) : null;
  const renderedContents = truncate ? renderTruncatedContents({
    truncateStringProps,
    truncateTooltip,
    renderedChildren,
    renderedExternalButtonIcon,
    use
  }) : renderedChildren;
  if (use === 'unstyled') {
    return /*#__PURE__*/_jsxs(UnstyledButtonLink, Object.assign({}, mergedProps, {
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      id: id,
      "aria-disabled": disabled,
      "aria-checked": ariaChecked,
      href: href,
      target: target,
      rel: rel,
      referrerPolicy: referrerPolicy,
      download: download,
      ping: ping,
      tabIndex: tabIndex,
      ref: linkElementRef,
      "data-button-use": use,
      $block: block,
      $tokenCssStyles: tokenCssStyles,
      $responsiveStyles: responsiveStyles,
      $isLinkUse: isLinkUse(use),
      $styleOverrides: newSharedStyleOverrides,
      $alwaysUnderline: shouldButtonBeUnderlined(use, theme),
      $isInDropdown: dropdownContext === null || dropdownContext === void 0 ? void 0 : dropdownContext.isDropdown,
      children: [renderedContents, !truncate && external && renderedExternalButtonIcon]
    }));
  }
  return /*#__PURE__*/_jsxs(StyledButtonLink, Object.assign({}, mergedProps, {
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    id: id,
    "aria-disabled": disabled,
    "aria-checked": ariaChecked,
    href: href,
    target: target,
    rel: rel,
    referrerPolicy: referrerPolicy,
    download: download,
    ping: ping,
    tabIndex: tabIndex,
    "data-button-use": use,
    $block: block,
    $variantCssStyles: useVariantCssStyles,
    $sizeCssStyles: sizeCssStyles,
    $tokenCssStyles: tokenCssStyles,
    $responsiveStyles: responsiveStyles,
    $typography: sizeTokens.typography,
    $isLinkUse: isLinkUse(use),
    $isTransparentUse: isTransparentUse(use),
    ref: linkElementRef,
    $styleOverrides: selectButtonOverrides,
    $use: use,
    $size: size,
    $alwaysUnderline: shouldButtonBeUnderlined(use, theme),
    $isInDropdown: dropdownContext === null || dropdownContext === void 0 ? void 0 : dropdownContext.isDropdown,
    children: [renderedContents, !truncate && external && renderedExternalButtonIcon]
  }));
});
PrivateButtonLink.displayName = 'PrivateButtonLink';